import React, { useCallback, useEffect, useState } from 'react';
import {
  listPendingChatQuestions,
  type PendingChatQuestion,
} from '../../../../api/chatQuestions';
import { getSocket } from '../../../../lib/socket';
import { formatDateTimeJST } from '../../../../utils/datetime';

const buildTargetUrl = (question: PendingChatQuestion): string => {
  const params = new URLSearchParams({ messageId: question.message_id });
  if (question.scope === 'event') {
    if (question.event_id) params.set('event', question.event_id);
    if (question.thread_id) params.set('thread', question.thread_id);
    params.set('view', 'chat');
    return `/artist-dashboard/event-chat?${params.toString()}`;
  }
  if (question.scope === 'group') {
    if (question.thread_id) params.set('thread', question.thread_id);
  }
  return `/artist-dashboard/chat?${params.toString()}`;
};

type Props = {
  refreshKey?: string | number;
};

type ChatQuestionSocketPayload = {
  question?: unknown;
};

export default function ChatQuestionPendingBar({ refreshKey = '' }: Props) {
  const [items, setItems] = useState<PendingChatQuestion[]>([]);

  const load = useCallback((signal?: AbortSignal) => {
    void listPendingChatQuestions({ signal })
      .then((result) => setItems(Array.isArray(result?.items) ? result.items : []))
      .catch(() => {
        // 背景取得の一過性失敗は無音。次の再表示・回答後更新で再取得する。
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshKey]);
  useEffect(() => {
    const handleAnswered: EventListener = (event) => {
      const detail = (event as CustomEvent<{ questionId?: string }>).detail;
      const questionId = String(detail?.questionId || '');
      if (questionId) {
        setItems((current) => current.filter((item) => String(item.id) !== questionId));
      } else {
        load();
      }
    };
    window.addEventListener('chat-question-answered', handleAnswered);
    return () => window.removeEventListener('chat-question-answered', handleAnswered);
  }, [load]);
  useEffect(() => {
    const socket = getSocket();
    const handleCreated = (payload: ChatQuestionSocketPayload = {}) => {
      if (payload?.question) load();
    };
    const handleCreatedEvent: EventListener = () => load();
    const eventNames = ['new_message', 'new_event_message', 'new_group_chat_message'];
    eventNames.forEach((eventName) => socket.on(eventName, handleCreated));
    window.addEventListener('chat-question-created', handleCreatedEvent);
    return () => {
      eventNames.forEach((eventName) => socket.off(eventName, handleCreated));
      window.removeEventListener('chat-question-created', handleCreatedEvent);
    };
  }, [load]);
  useEffect(() => {
    if (!items.length) return undefined;
    const nextDeadline = Math.min(
      ...items
        .map((item) => new Date(item.response_deadline_at).getTime())
        .filter(Number.isFinite),
    );
    if (!Number.isFinite(nextDeadline)) return undefined;
    const removeExpired = () => {
      const now = Date.now();
      setItems((current) => current.filter(
        (item) => new Date(item.response_deadline_at).getTime() > now,
      ));
    };
    if (nextDeadline <= Date.now()) {
      removeExpired();
      return undefined;
    }
    const timer = window.setTimeout(
      removeExpired,
      Math.min(nextDeadline - Date.now() + 100, 24 * 60 * 60 * 1000),
    );
    return () => window.clearTimeout(timer);
  }, [items]);
  if (!items.length) return null;
  const first = items[0];

  const jump = () => {
    const rawMessageId = String(first.message_id || '');
    const escapedMessageId = globalThis.CSS?.escape
      ? globalThis.CSS.escape(rawMessageId)
      : rawMessageId.replace(/["\\]/g, '\\$&');
    const local = document.querySelector(`[data-mid="${escapedMessageId}"]`);
    if (local) {
      local.scrollIntoView({ behavior: 'smooth', block: 'center' });
      local.classList.add('is-anchor-hit');
      window.setTimeout(() => local.classList.remove('is-anchor-hit'), 1800);
      return;
    }
    window.location.assign(buildTargetUrl(first));
  };

  return (
    <button type="button" className="chat-question-pending-bar" onClick={jump}>
      <span className="chat-question-pending-bar__icon" aria-hidden="true">
        <i className="fa-solid fa-circle-question" />
      </span>
      <span className="chat-question-pending-bar__body">
        <strong>未回答の質問メッセージがあります</strong>
        <span>
          {first.question_text} ・ 期限 {formatDateTimeJST(first.response_deadline_at)}
          {items.length > 1 ? `（ほか${items.length - 1}件）` : ''}
        </span>
      </span>
      <i className="fa-solid fa-chevron-right" aria-hidden="true" />
    </button>
  );
}
